import {type ReactNode, useEffect, useState} from 'react'
import {
  Animated,
  Dimensions,
  type DimensionValue,
  Easing,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import * as Haptics from 'expo-haptics'
import {LinearGradient} from 'expo-linear-gradient'
import {tokens} from '@bsky.app/alf'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {type FillBlank, type Slide, type SlideResult} from '#/types/capsule'
import {SLIDE_TYPE_META} from '#/types/capsule'

const {space, fontSize, borderRadius} = tokens
const confettiColors = [
  '#0085ff',
  '#59b9ff',
  '#22c55e',
  '#4ade80',
  '#f59e0b',
  '#a855f7',
  '#ec4899',
  '#7dd3fc',
  '#ff6a56',
  '#ffd700',
]

// ─── Per-theme visual tokens ──────────────────────────────────────────────────

interface ThemeToken {
  /** Root background colour (used when rootGradient is absent) */
  rootBg: string
  /** If defined, wraps the root in a LinearGradient instead of a plain View */
  rootGradient?: readonly [string, string, string]
  cardBg: string
  cardBorder: string
  /** true → theme has a light background; switches text to dark palette */
  isLight: boolean
  textPrimary: string
  textSecondary: string
  textMuted: string
  scoreBarTrackBg: string
  scoreBarBorder: string
  statRowBorder: string
  statDivider: string
  statLabel: string
  replayBorder: string
  replayBg: string
  replayText: string
  breakdownBorder: string
  breakdownHeaderBg: string
  answerCompareBg: string
  answerCompareBorder: string
  answerLabel: string
  confetti: readonly string[]
  /** Floating decorative emojis rendered behind the card (e.g. summer theme) */
  decorEmojis?: string[]
}

const DEFAULT_TOK: ThemeToken = {
  rootBg: '#0a1220',
  cardBg: '#111c2c',
  cardBorder: '#1e3048',
  isLight: false,
  textPrimary: '#e8f1f8',
  textSecondary: '#a9bccf',
  textMuted: '#54687d',
  scoreBarTrackBg: 'rgba(255,255,255,0.07)',
  scoreBarBorder: 'rgba(255,255,255,0)',
  statRowBorder: '#1e3048',
  statDivider: '#1e3048',
  statLabel: '#6b8299',
  replayBorder: '#1e3048',
  replayBg: 'transparent',
  replayText: '#e8f1f8',
  breakdownBorder: '#1e3048',
  breakdownHeaderBg: 'rgba(255,255,255,0.03)',
  answerCompareBg: 'rgba(255,255,255,0.03)',
  answerCompareBorder: 'rgba(255,255,255,0.07)',
  answerLabel: 'rgba(255,255,255,0.35)',
  confetti: confettiColors,
}

const THEME_TOKENS: Record<string, ThemeToken> = {
  'classic-quiz': DEFAULT_TOK,

  summer: {
    rootBg: '#FFF8E7',
    rootGradient: ['#FFFBEF', '#FFE9A0', '#FDD860'] as const,
    cardBg: '#FFFCEF',
    cardBorder: '#F0C730',
    isLight: true,
    textPrimary: '#3B2200',
    textSecondary: '#7A5200',
    textMuted: '#A07830',
    scoreBarTrackBg: 'rgba(0,0,0,0.07)',
    scoreBarBorder: 'rgba(0,0,0,0.09)',
    statRowBorder: '#E8C540',
    statDivider: '#E8C540',
    statLabel: '#A07830',
    replayBorder: '#3B2200',
    replayBg: 'transparent',
    replayText: '#3B2200',
    breakdownBorder: '#E8C540',
    breakdownHeaderBg: 'rgba(0,0,0,0.04)',
    answerCompareBg: 'rgba(0,0,0,0.04)',
    answerCompareBorder: 'rgba(0,0,0,0.09)',
    answerLabel: 'rgba(59,34,0,0.4)',
    confetti: [
      '#FF6B35',
      '#FFD700',
      '#22c55e',
      '#FF1493',
      '#00BFFF',
      '#FFA500',
      '#32CD32',
      '#FF69B4',
    ],
    decorEmojis: ['☀️', '🌊', '🌺', '🏖️', '🌴', '⭐️'],
  },

  'liquid-glass': {
    rootBg: '#12022a',
    rootGradient: ['#1e0540', '#12022a', '#0a0118'] as const,
    cardBg: 'rgba(255,255,255,0.07)',
    cardBorder: 'rgba(255,255,255,0.18)',
    isLight: false,
    textPrimary: '#f3e8ff',
    textSecondary: 'rgba(243,232,255,0.7)',
    textMuted: 'rgba(243,232,255,0.38)',
    scoreBarTrackBg: 'rgba(255,255,255,0.08)',
    scoreBarBorder: 'rgba(255,255,255,0.1)',
    statRowBorder: 'rgba(255,255,255,0.12)',
    statDivider: 'rgba(255,255,255,0.1)',
    statLabel: 'rgba(243,232,255,0.42)',
    replayBorder: 'rgba(255,255,255,0.2)',
    replayBg: 'rgba(255,255,255,0.06)',
    replayText: '#f3e8ff',
    breakdownBorder: 'rgba(255,255,255,0.12)',
    breakdownHeaderBg: 'rgba(255,255,255,0.04)',
    answerCompareBg: 'rgba(255,255,255,0.04)',
    answerCompareBorder: 'rgba(255,255,255,0.08)',
    answerLabel: 'rgba(243,232,255,0.38)',
    confetti: [
      '#c4b5fd',
      '#8b5cf6',
      '#ddd6fe',
      '#7c3aed',
      '#f3e8ff',
      '#a78bfa',
      '#ede9fe',
      '#6d28d9',
    ],
  },

  'html-bridge': {
    rootBg: '#051210',
    cardBg: '#0a1e1c',
    cardBorder: '#1c4040',
    isLight: false,
    textPrimary: '#e0faf6',
    textSecondary: 'rgba(200,250,240,0.65)',
    textMuted: 'rgba(200,250,240,0.35)',
    scoreBarTrackBg: 'rgba(20,184,166,0.1)',
    scoreBarBorder: 'rgba(20,184,166,0.15)',
    statRowBorder: '#1c4040',
    statDivider: '#1c4040',
    statLabel: 'rgba(200,250,240,0.4)',
    replayBorder: '#1c4040',
    replayBg: 'transparent',
    replayText: '#e0faf6',
    breakdownBorder: '#1c4040',
    breakdownHeaderBg: 'rgba(20,184,166,0.04)',
    answerCompareBg: 'rgba(20,184,166,0.04)',
    answerCompareBorder: 'rgba(20,184,166,0.08)',
    answerLabel: 'rgba(200,250,240,0.38)',
    confetti: [
      '#14b8a6',
      '#2dd4bf',
      '#99f6e4',
      '#0d9488',
      '#5eead4',
      '#ccfbf1',
      '#0f766e',
      '#a7f3d0',
    ],
  },
}

const {width: SCREEN_W, height: SCREEN_H} = Dimensions.get('window')

const PARTICLE_COUNT = 36

interface Particle {
  x: Animated.Value
  y: Animated.Value
  rotate: Animated.Value
  opacity: Animated.Value
  color: string
  size: number
  shape: 'circle' | 'square' | 'rect'
}

function createParticles(colors: readonly string[]): Particle[] {
  return Array.from({length: PARTICLE_COUNT}, (_, i) => ({
    x: new Animated.Value(Math.random() * SCREEN_W),
    y: new Animated.Value(-30 - Math.random() * 120),
    rotate: new Animated.Value(0),
    opacity: new Animated.Value(1),
    color: colors[i % colors.length],
    size: 6 + Math.random() * 8,
    shape: (['circle', 'square', 'rect'] as const)[i % 3],
  }))
}

function Confetti({colors = confettiColors}: {colors?: readonly string[]}) {
  const [particles] = useState<Particle[]>(() => createParticles(colors))

  useEffect(() => {
    particles.forEach((p, i) => {
      const duration = 1800 + Math.random() * 1400
      const delay = i * 55

      const loop = () => {
        p.x.setValue(Math.random() * SCREEN_W)
        p.y.setValue(-40 - Math.random() * 60)
        p.rotate.setValue(0)
        p.opacity.setValue(1)

        Animated.parallel([
          Animated.timing(p.y, {
            toValue: SCREEN_H + 60,
            duration,
            delay,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
          Animated.timing(p.rotate, {
            toValue: 1,
            duration,
            delay,
            easing: Easing.linear,
            useNativeDriver: false,
          }),
          Animated.timing(p.x, {
            toValue:
              parseFloat(
                String((p.x as Animated.Value & {_value?: number})._value ?? 0),
              ) +
              (Math.random() - 0.5) * 120,
            duration,
            delay,
            easing: Easing.inOut(Easing.sin),
            useNativeDriver: false,
          }),
          Animated.sequence([
            Animated.delay(delay + duration * 0.7),
            Animated.timing(p.opacity, {
              toValue: 0,
              duration: duration * 0.3,
              easing: Easing.in(Easing.quad),
              useNativeDriver: false,
            }),
          ]),
        ]).start(() => loop())
      }

      const t = setTimeout(loop, delay)
      return () => clearTimeout(t)
    })
  }, [])

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      {particles.map((p, i) => {
        const rotation = p.rotate.interpolate({
          inputRange: [0, 1],
          outputRange: ['0deg', `${180 + Math.random() * 360}deg`],
        })

        const shape =
          p.shape === 'circle'
            ? {borderRadius: p.size / 2}
            : p.shape === 'rect'
              ? {borderRadius: 2, width: p.size * 1.6, height: p.size * 0.6}
              : {borderRadius: 2}

        return (
          <Animated.View
            key={i}
            style={[
              {
                position: 'absolute',
                width: p.size,
                height: p.size,
                backgroundColor: p.color,
                transform: [
                  {translateX: p.x},
                  {translateY: p.y},
                  {rotate: rotation},
                ],
                opacity: p.opacity,
              },
              shape,
            ]}
          />
        )
      })}
    </View>
  )
}

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (target === 0) return
    const steps = 40
    const step = target / steps
    const interval = duration / steps
    let current = 0
    const timer = setInterval(() => {
      current = Math.min(current + step, target)
      setValue(Math.round(current))
      if (current >= target) clearInterval(timer)
    }, interval)
    return () => clearInterval(timer)
  }, [target, duration])
  return value
}

function getGrade(pct: number): {emoji: string; color: string} {
  if (pct === 100) return {emoji: '🏆', color: '#ffd700'}
  if (pct >= 80) return {emoji: '🌟', color: '#22c55e'}
  if (pct >= 60) return {emoji: '👏', color: '#0085ff'}
  if (pct >= 40) return {emoji: '💪', color: '#f59e0b'}
  return {emoji: '🎯', color: '#ef4444'}
}

interface Props {
  score: number
  total: number
  totalPts?: number
  maxTotalPts?: number
  primaryColor: string
  capsuleTitle: string
  onReplay: () => void
  onDone: () => void
  timeTakenMs?: number
  results?: SlideResult[]
  slides?: Slide[]
  footer?: ReactNode
  /** Capsule themeId — drives the visual skin of the result page */
  themeId?: string
}

function formatTime(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`
}

// ─── Answer formatters ────────────────────────────────────────────────────────

function formatUserAnswer(slide: Slide, userAnswer: unknown): string {
  if (userAnswer == null) return '—'
  try {
    switch (slide.type) {
      case 'single_choice':
      case 'image_choice': {
        const opt = slide.options?.find(o => o.id === userAnswer)
        return opt?.label ?? String(userAnswer)
      }
      case 'true_false':
        return userAnswer === true || userAnswer === 'true' ? 'True' : 'False'
      case 'multiple_choice': {
        const ids = Array.isArray(userAnswer) ? userAnswer : []
        const labels = ids.map(
          id => slide.options?.find(o => o.id === id)?.label ?? id,
        )
        return labels.join(', ') || '—'
      }
      case 'ordering': {
        const ids = Array.isArray(userAnswer) ? (userAnswer as string[]) : []
        return (
          ids
            .map(id => slide.orderItems?.find(i => i.id === id)?.label ?? id)
            .join(' → ') || '—'
        )
      }
      case 'drag_words':
      case 'fill_blanks':
      case 'complex_fill_blank': {
        const vals = Array.isArray(userAnswer)
          ? (userAnswer as (string | null)[])
          : []
        return vals.filter(Boolean).join(', ') || '—'
      }
      case 'mark_words': {
        const indices = Array.isArray(userAnswer)
          ? (userAnswer as number[])
          : []
        const wordTokens = (slide.markWordsText ?? '')
          .split(/\s+/)
          .filter(Boolean)
        return (
          indices
            .map(i => wordTokens[i])
            .filter(Boolean)
            .join(', ') || '—'
        )
      }
      case 'find_words': {
        const words = Array.isArray(userAnswer) ? (userAnswer as string[]) : []
        return words.join(', ') || '—'
      }
      case 'matching': {
        if (typeof userAnswer !== 'object' || !userAnswer) return '—'
        const matched = userAnswer as Record<string, string>
        return (
          Object.entries(matched)
            .map(([leftId, rightId]) => {
              const leftLabel =
                slide.pairs?.find(p => p.id === leftId)?.left ?? leftId
              const rightLabel =
                slide.pairs?.find(p => p.id === rightId)?.right ?? rightId
              return `${leftLabel} → ${rightLabel}`
            })
            .join('; ') || '—'
        )
      }
      default:
        return '—'
    }
  } catch {
    return '—'
  }
}

function formatCorrectAnswer(slide: Slide): string {
  try {
    switch (slide.type) {
      case 'single_choice':
      case 'image_choice': {
        const opt = slide.options?.find(o => o.isCorrect)
        return opt?.label ?? '—'
      }
      case 'true_false':
        return slide.answer === 'true' ? 'True' : 'False'
      case 'multiple_choice':
        return (
          slide.options
            ?.filter(o => o.isCorrect)
            .map(o => o.label)
            .join(', ') || '—'
        )
      case 'ordering':
        return slide.orderItems?.map(i => i.label).join(' → ') || '—'
      case 'drag_words': {
        const matches = [
          ...(slide.dragWordsTemplate ?? '').matchAll(/\[([^\]]+)\]/g),
        ]
        return matches.map(m => m[1]).join(', ') || '—'
      }
      case 'fill_blanks':
        return slide.blanks?.map((b: FillBlank) => b.answer).join(', ') || '—'
      case 'mark_words':
        return slide.markWordsCorrect?.join(', ') || '—'
      case 'complex_fill_blank':
        return slide.complexBlanks?.map(b => b.answer).join(', ') || '—'
      case 'find_words':
        return slide.findWordsTargets?.join(', ') || '—'
      case 'matching':
        return slide.pairs?.map(p => `${p.left} → ${p.right}`).join('; ') || '—'
      default:
        return '—'
    }
  } catch {
    return '—'
  }
}

export function CompletionScreen({
  score,
  total,
  totalPts,
  maxTotalPts,
  primaryColor,
  capsuleTitle,
  onReplay,
  onDone,
  timeTakenMs,
  results,
  slides,
  footer,
  themeId,
}: Props) {
  const {t: l} = useLingui()
  const insets = useSafeAreaInsets()
  const tok = THEME_TOKENS[themeId ?? ''] ?? DEFAULT_TOK

  const hasExercises = maxTotalPts != null ? maxTotalPts > 0 : total > 0

  const pct = hasExercises
    ? Math.min(
        100,
        Math.max(
          0,
          maxTotalPts && maxTotalPts > 0
            ? Math.round(((totalPts ?? 0) / maxTotalPts) * 100)
            : total > 0
              ? Math.round((score / total) * 100)
              : 0,
        ),
      )
    : 100

  const usePointsMode = !!(maxTotalPts && maxTotalPts > 0)

  const gradeLabel = (() => {
    if (!hasExercises) return l`Completed!`
    if (pct === 100) return l`Perfect!`
    if (pct >= 80) return l`Excellent!`
    if (pct >= 60) return l`Great job!`
    if (pct >= 40) return l`Keep going!`
    return l`Nice try!`
  })()

  const gradeSubtitle = (() => {
    if (!hasExercises) return l`You finished the capsule. Great focus!`
    if (pct === 100) return l`Flawless run — every question nailed.`
    if (pct >= 80) return l`You really know your stuff.`
    if (pct >= 60) return l`Solid work — a couple more reps and you're golden.`
    if (pct >= 40)
      return l`You're getting there — try again for a better score.`
    return l`Every attempt makes you sharper. Give it another go.`
  })()

  const grade = hasExercises
    ? getGrade(pct)
    : {
        emoji: '🎓',
        color: primaryColor || '#0085ff',
      }
  const displayPct = useCountUp(pct, 1100)

  const [cardScale] = useState(() => new Animated.Value(0.92))
  const [cardOpacity] = useState(() => new Animated.Value(0))
  const [emojiScale] = useState(() => new Animated.Value(0))
  const [emojiSpin] = useState(() => new Animated.Value(0))
  const [subtitleOpacity] = useState(() => new Animated.Value(0))
  const [statsOpacity] = useState(() => new Animated.Value(0))
  const [statsY] = useState(() => new Animated.Value(12))
  const [glowPulse] = useState(() => new Animated.Value(0.6))

  useEffect(() => {
    Haptics.notificationAsync(
      pct >= 60
        ? Haptics.NotificationFeedbackType.Success
        : Haptics.NotificationFeedbackType.Warning,
    )

    Animated.sequence([
      Animated.parallel([
        Animated.spring(cardScale, {
          toValue: 1,
          useNativeDriver: false,
          tension: 200,
          friction: 14,
        }),
        Animated.timing(cardOpacity, {
          toValue: 1,
          duration: 280,
          useNativeDriver: false,
        }),
      ]),
      Animated.parallel([
        Animated.spring(emojiScale, {
          toValue: 1,
          useNativeDriver: false,
          tension: 180,
          friction: 8,
        }),
        Animated.timing(emojiSpin, {
          toValue: 1,
          duration: 500,
          easing: Easing.out(Easing.back(1.4)),
          useNativeDriver: false,
        }),
      ]),
      Animated.timing(subtitleOpacity, {
        toValue: 1,
        duration: 350,
        useNativeDriver: false,
      }),
      Animated.parallel([
        Animated.timing(statsOpacity, {
          toValue: 1,
          duration: 350,
          useNativeDriver: false,
        }),
        Animated.timing(statsY, {
          toValue: 0,
          duration: 350,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: false,
        }),
      ]),
    ]).start()

    Animated.loop(
      Animated.sequence([
        Animated.timing(glowPulse, {
          toValue: 1,
          duration: 1600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
        Animated.timing(glowPulse, {
          toValue: 0.6,
          duration: 1600,
          easing: Easing.inOut(Easing.sin),
          useNativeDriver: false,
        }),
      ]),
    ).start()
  }, [])

  const emojiRotate = emojiSpin.interpolate({
    inputRange: [0, 1],
    outputRange: ['-25deg', '0deg'],
  })

  // Shared inner content — kept outside any wrapper so Animated.Values survive re-renders
  const inner = (
    <>
      {pct >= 60 && <Confetti colors={tok.confetti} />}

      {/* Ambient glow */}
      <Animated.View
        pointerEvents="none"
        style={[
          styles.glow,
          {
            backgroundColor: grade.color,
            opacity: glowPulse.interpolate({
              inputRange: [0.6, 1],
              outputRange: [0.07, 0.18],
            }),
          },
        ]}
      />

      {/* Decorative floating emojis — summer theme */}
      {tok.decorEmojis &&
        tok.decorEmojis.map((emoji, i) => (
          <Text
            key={i}
            pointerEvents="none"
            style={[
              styles.decorEmoji,
              {
                top: `${[14, 40, 22, 60, 30, 8][i % 6]}%`,
                left:
                  i % 2 === 0 ? `${[4, 10, 3, 8, 5, 2][i % 6]}%` : undefined,
                right:
                  i % 2 !== 0 ? `${[4, 8, 5, 6, 3, 7][i % 6]}%` : undefined,
                fontSize: [32, 26, 22, 28, 20, 24][i % 6],
                opacity: 0.18 + (i % 3) * 0.06,
                transform: [
                  {rotate: `${[-12, 15, -8, 20, -15, 10][i % 6]}deg`},
                ],
              },
            ]}>
            {emoji}
          </Text>
        ))}

      <ScrollView
        style={{flex: 1, width: '100%'}}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: insets.top + space.lg,
            paddingBottom: insets.bottom + space.xl,
          },
        ]}
        showsVerticalScrollIndicator={false}>
        <Animated.View
          style={[
            styles.card,
            {
              backgroundColor: tok.cardBg,
              borderColor: tok.cardBorder,
              // Tone down shadow on light-background themes
              shadowColor: tok.isLight ? '#b8860b' : '#000',
              shadowOpacity: tok.isLight ? 0.14 : 0.55,
            },
            {transform: [{scale: cardScale}], opacity: cardOpacity},
          ]}>
          <LinearGradient
            colors={[grade.color + '1a', 'transparent']}
            style={styles.cardTopGlow}
            pointerEvents="none"
          />

          {/* Emoji + grade */}
          <Animated.Text
            style={[
              styles.emoji,
              {transform: [{scale: emojiScale}, {rotate: emojiRotate}]},
            ]}>
            {grade.emoji}
          </Animated.Text>
          <Text style={[styles.gradeLabel, {color: grade.color}]}>
            {gradeLabel}
          </Text>
          <Animated.Text
            style={[
              styles.subtitle,
              {opacity: subtitleOpacity, color: tok.textSecondary},
            ]}>
            {gradeSubtitle}
          </Animated.Text>
          <Text
            style={[styles.capsuleTitle, {color: tok.textMuted}]}
            numberOfLines={2}>
            {capsuleTitle}
          </Text>

          {/* ── Score bar ─────────────────────────────────────────────────── */}
          <View style={styles.scoreBarWrap}>
            <View style={styles.scoreBarHeader}>
              <Text style={[styles.scoreBarPct, {color: grade.color}]}>
                {displayPct}
                <Text
                  style={[styles.scoreBarSign, {color: grade.color + '99'}]}>
                  %
                </Text>
              </Text>
              {hasExercises && (
                <Text
                  style={[styles.scoreBarSubtext, {color: grade.color + '99'}]}>
                  {usePointsMode
                    ? l`${totalPts ?? 0} / ${maxTotalPts ?? 0} pts`
                    : l`${score} / ${total} correct`}
                </Text>
              )}
            </View>
            <View
              style={[
                styles.scoreBarTrack,
                {
                  backgroundColor: tok.scoreBarTrackBg,
                  borderColor: grade.color + '28',
                },
              ]}>
              <View
                style={[
                  styles.scoreBarFill,
                  {
                    width: `${displayPct}%` as DimensionValue,
                    backgroundColor: grade.color,
                  },
                ]}
              />
              {[25, 50, 75].map(tick => (
                <View
                  key={tick}
                  style={[
                    styles.scoreBarTick,
                    {left: `${tick}%` as DimensionValue},
                  ]}
                />
              ))}
            </View>
          </View>

          {/* Stats row */}
          <Animated.View
            style={{
              width: '100%',
              opacity: statsOpacity,
              transform: [{translateY: statsY}],
            }}>
            {hasExercises ? (
              <View style={[styles.statRow, {borderColor: tok.statRowBorder}]}>
                {usePointsMode ? (
                  <>
                    <StatChip
                      label={l`Points`}
                      value={`${totalPts ?? 0}`}
                      color="#22c55e"
                      labelColor={tok.statLabel}
                    />
                    <View
                      style={[
                        styles.divider,
                        {backgroundColor: tok.statDivider},
                      ]}
                    />
                    <StatChip
                      label={l`Max`}
                      value={`${maxTotalPts ?? 0}`}
                      color={tok.statLabel}
                      labelColor={tok.statLabel}
                    />
                    <View
                      style={[
                        styles.divider,
                        {backgroundColor: tok.statDivider},
                      ]}
                    />
                    <StatChip
                      label={l`Score`}
                      value={`${pct}%`}
                      color={grade.color}
                      labelColor={tok.statLabel}
                    />
                    {timeTakenMs != null && (
                      <>
                        <View
                          style={[
                            styles.divider,
                            {backgroundColor: tok.statDivider},
                          ]}
                        />
                        <StatChip
                          label={l`Time`}
                          value={formatTime(timeTakenMs)}
                          color={tok.statLabel}
                          labelColor={tok.statLabel}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <StatChip
                      label={l`Correct`}
                      value={`${score}/${total}`}
                      color="#22c55e"
                      labelColor={tok.statLabel}
                    />
                    <View
                      style={[
                        styles.divider,
                        {backgroundColor: tok.statDivider},
                      ]}
                    />
                    <StatChip
                      label={l`Wrong`}
                      value={`${total - score}/${total}`}
                      color="#ef4444"
                      labelColor={tok.statLabel}
                    />
                    <View
                      style={[
                        styles.divider,
                        {backgroundColor: tok.statDivider},
                      ]}
                    />
                    <StatChip
                      label={l`Score`}
                      value={`${pct}%`}
                      color={grade.color}
                      labelColor={tok.statLabel}
                    />
                    {timeTakenMs != null && (
                      <>
                        <View
                          style={[
                            styles.divider,
                            {backgroundColor: tok.statDivider},
                          ]}
                        />
                        <StatChip
                          label={l`Time`}
                          value={formatTime(timeTakenMs)}
                          color={tok.statLabel}
                          labelColor={tok.statLabel}
                        />
                      </>
                    )}
                  </>
                )}
              </View>
            ) : (
              <View style={[styles.statRow, {borderColor: tok.statRowBorder}]}>
                <StatChip
                  label={l`Slides`}
                  value={`${total}`}
                  color={grade.color}
                  labelColor={tok.statLabel}
                />
                {timeTakenMs != null && (
                  <>
                    <View
                      style={[
                        styles.divider,
                        {backgroundColor: tok.statDivider},
                      ]}
                    />
                    <StatChip
                      label={l`Time`}
                      value={formatTime(timeTakenMs)}
                      color={tok.statLabel}
                      labelColor={tok.statLabel}
                    />
                  </>
                )}
                <View
                  style={[styles.divider, {backgroundColor: tok.statDivider}]}
                />
                <StatChip
                  label={l`Status`}
                  value={l`Done ✓`}
                  color="#22c55e"
                  labelColor={tok.statLabel}
                />
              </View>
            )}
          </Animated.View>

          {/* Action buttons */}
          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                Haptics.selectionAsync()
                onReplay()
              }}
              style={[
                styles.replayBtn,
                {borderColor: tok.replayBorder, backgroundColor: tok.replayBg},
              ]}>
              <Text style={[styles.replayText, {color: tok.replayText}]}>
                <Trans>↺ Play again</Trans>
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium)
                onDone()
              }}
              style={[styles.doneBtn, {backgroundColor: primaryColor}]}>
              <Text style={styles.doneBtnText}>
                <Trans>Done →</Trans>
              </Text>
            </Pressable>
          </View>

          {/* Optional footer (e.g. teacher publish controls) */}
          {footer ? <View style={styles.footer}>{footer}</View> : null}

          {/* Per-slide answer breakdown */}
          {results &&
            slides &&
            results.filter(r => r.isCorrect !== undefined).length > 0 && (
              <AnswerBreakdown
                results={results}
                slides={slides}
                primaryColor={primaryColor}
                tok={tok}
              />
            )}
        </Animated.View>
      </ScrollView>
    </>
  )

  // Render gradient root for themes that need it, plain View otherwise.
  // Do NOT wrap `inner` in an inline component — that would remount children
  // on every render and reset all Animated.Values.
  if (tok.rootGradient) {
    return (
      <LinearGradient colors={tok.rootGradient} style={styles.root}>
        {inner}
      </LinearGradient>
    )
  }
  return (
    <View style={[styles.root, {backgroundColor: tok.rootBg}]}>{inner}</View>
  )
}

// ─── Answer Breakdown ─────────────────────────────────────────────────────────

function AnswerBreakdown({
  results,
  slides,
  primaryColor,
  tok,
}: {
  results: SlideResult[]
  slides: Slide[]
  primaryColor: string
  tok: ThemeToken
}) {
  const {t: l} = useLingui()
  const [open, setOpen] = useState(false)
  const answered = results.filter(r => r.isCorrect !== undefined)
  if (answered.length === 0) return null

  const iconMuted = tok.isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.55)'
  const chevronMuted = tok.isLight
    ? 'rgba(0,0,0,0.28)'
    : 'rgba(255,255,255,0.35)'
  const correctAnswerItalic = tok.isLight
    ? 'rgba(0,0,0,0.38)'
    : 'rgba(255,255,255,0.45)'

  return (
    <View style={[styles.breakdownWrap, {borderColor: tok.breakdownBorder}]}>
      <TouchableOpacity
        accessibilityRole="button"
        onPress={() => setOpen(o => !o)}
        style={[
          styles.breakdownHeader,
          {backgroundColor: tok.breakdownHeaderBg},
        ]}
        activeOpacity={0.7}>
        <Feather name="list" size={14} color={iconMuted} />
        <Text style={[styles.breakdownTitle, {color: iconMuted}]}>
          {l`Review answers (${answered.length})`}
        </Text>
        <Feather
          name={open ? 'chevron-up' : 'chevron-down'}
          size={14}
          color={chevronMuted}
        />
      </TouchableOpacity>

      {open && (
        <View style={styles.breakdownList}>
          {answered.map((r, i) => {
            const slide = slides.find(sl => sl.id === r.slideId)
            const meta = slide ? SLIDE_TYPE_META[slide.type] : null
            const isCorrect = r.isCorrect === true
            const isPartial = !isCorrect && r.points != null && r.points > 0
            const color = isCorrect
              ? '#22c55e'
              : isPartial
                ? '#f59e0b'
                : '#ef4444'
            const icon = isCorrect
              ? 'check-circle'
              : isPartial
                ? 'alert-circle'
                : 'x-circle'
            const label =
              slide?.question || slide?.contentTitle || meta?.label || 'Slide'
            const userAns = slide ? formatUserAnswer(slide, r.userAnswer) : '—'
            const correctAns = slide ? formatCorrectAnswer(slide) : '—'
            const showAnswers = slide && !isCorrect && correctAns !== '—'

            return (
              <View
                key={r.slideId}
                style={[styles.breakdownRow, {borderColor: color + '2a'}]}>
                <Feather
                  name={icon}
                  size={15}
                  color={color}
                  style={{marginTop: 1}}
                />
                <View style={{flex: 1, gap: 5}}>
                  <Text
                    style={[styles.breakdownQ, {color: tok.textPrimary}]}
                    numberOfLines={3}>
                    {`${i + 1}. ${label}`}
                  </Text>
                  {meta && (
                    <Text style={[styles.breakdownType, {color: primaryColor}]}>
                      {meta.label}
                    </Text>
                  )}

                  {showAnswers && (
                    <View
                      style={[
                        styles.answerCompare,
                        {
                          backgroundColor: tok.answerCompareBg,
                          borderColor: tok.answerCompareBorder,
                        },
                      ]}>
                      <View style={styles.answerRow}>
                        <Feather
                          name="user"
                          size={10}
                          color={tok.answerLabel}
                        />
                        <Text
                          style={[
                            styles.answerLabel,
                            {color: tok.answerLabel},
                          ]}>
                          {l`Your answer:`}
                        </Text>
                        <Text
                          style={[
                            styles.answerValue,
                            {color: isPartial ? '#f59e0b' : '#ef4444'},
                          ]}
                          numberOfLines={2}>
                          {userAns}
                        </Text>
                      </View>
                      <View style={styles.answerRow}>
                        <Feather name="check" size={10} color="#22c55e" />
                        <Text
                          style={[
                            styles.answerLabel,
                            {color: tok.answerLabel},
                          ]}>
                          {l`Correct:`}
                        </Text>
                        <Text
                          style={[styles.answerValue, {color: '#22c55e'}]}
                          numberOfLines={2}>
                          {correctAns}
                        </Text>
                      </View>
                    </View>
                  )}

                  {isCorrect && userAns !== '—' && (
                    <View style={styles.answerRow}>
                      <Feather name="check" size={10} color="#22c55e" />
                      <Text
                        style={[
                          styles.answerValue,
                          {color: correctAnswerItalic, fontStyle: 'italic'},
                        ]}
                        numberOfLines={1}>
                        {userAns}
                      </Text>
                    </View>
                  )}
                </View>

                <Text style={[styles.breakdownPts, {color}]}>
                  {r.points ?? 0}pt{r.points !== 1 ? 's' : ''}
                </Text>
              </View>
            )
          })}
        </View>
      )}
    </View>
  )
}

function StatChip({
  label,
  value,
  color,
  labelColor,
}: {
  label: string
  value: string
  color: string
  labelColor?: string
}) {
  return (
    <View style={styles.statChip}>
      <Text style={[styles.statValue, {color}]}>{value}</Text>
      <Text
        style={[
          styles.statLabel,
          labelColor ? {color: labelColor} : undefined,
        ]}>
        {label}
      </Text>
    </View>
  )
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#0a1220',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'stretch',
    paddingHorizontal: space.md,
  },
  glow: {
    position: 'absolute',
    width: SCREEN_W * 1.4,
    height: SCREEN_W * 1.4,
    borderRadius: SCREEN_W * 0.7,
    top: SCREEN_H / 2 - (SCREEN_W * 1.4) / 2,
  },
  card: {
    width: '100%',
    backgroundColor: '#111c2c',
    borderRadius: borderRadius.xl,
    borderWidth: 1,
    borderColor: '#1e3048',
    padding: space._2xl,
    alignItems: 'center',
    gap: space.lg,
    shadowColor: '#000',
    shadowOpacity: 0.55,
    shadowRadius: 28,
    shadowOffset: {width: 0, height: 10},
    elevation: 14,
    overflow: 'hidden',
  },
  cardTopGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 160,
  },
  emoji: {
    fontSize: 60,
    lineHeight: 74,
  },
  gradeLabel: {
    fontSize: fontSize._2xl,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: fontSize.sm,
    color: '#a9bccf',
    textAlign: 'center',
    lineHeight: 19,
    marginTop: -space.md,
    paddingHorizontal: space.sm,
  },
  capsuleTitle: {
    fontSize: fontSize._2xs,
    color: '#54687d',
    textAlign: 'center',
    lineHeight: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ── Score bar ────────────────────────────────────────────────────────────────
  scoreBarWrap: {
    width: '100%',
    gap: 10,
  },
  scoreBarHeader: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    paddingHorizontal: 2,
  },
  scoreBarPct: {
    fontSize: 44,
    fontWeight: '900',
    lineHeight: 50,
    letterSpacing: -1,
  },
  scoreBarSign: {
    fontSize: fontSize.md,
    fontWeight: '700',
  },
  scoreBarSubtext: {
    fontSize: fontSize.sm,
    fontWeight: '600',
    marginBottom: 8,
  },
  scoreBarTrack: {
    width: '100%',
    height: 14,
    borderRadius: 7,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  scoreBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 7,
  },
  scoreBarTick: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },

  // ── Stats row ────────────────────────────────────────────────────────────────
  statRow: {
    flexDirection: 'row',
    width: '100%',
    borderWidth: 1,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  statChip: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: space.sm,
    gap: 2,
  },
  statValue: {
    fontSize: fontSize.md,
    fontWeight: '800',
  },
  statLabel: {
    fontSize: fontSize._2xs,
    color: '#6b8299',
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  divider: {
    width: 1,
    backgroundColor: '#1e3048',
  },

  // ── Actions ──────────────────────────────────────────────────────────────────
  actions: {
    flexDirection: 'row',
    gap: space.sm,
    width: '100%',
    marginTop: space.xs,
  },
  replayBtn: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: borderRadius.full,
    borderWidth: 1,
    borderColor: '#1e3048',
    alignItems: 'center',
  },
  replayText: {
    color: '#e8f1f8',
    fontWeight: '700',
    fontSize: fontSize.sm,
  },
  doneBtn: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: borderRadius.full,
    alignItems: 'center',
  },
  doneBtnText: {
    color: '#fff',
    fontWeight: '800',
    fontSize: fontSize.sm,
  },

  // ── Footer (teacher preview controls, etc.) ──────────────────────────────────
  footer: {
    width: '100%',
    marginTop: space.sm,
    gap: space.sm,
  },

  // ── Answer breakdown ─────────────────────────────────────────────────────────
  breakdownWrap: {
    width: '100%',
    marginTop: space.xs,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#1e3048',
    overflow: 'hidden',
  },
  breakdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  breakdownTitle: {
    flex: 1,
    color: 'rgba(255,255,255,0.55)',
    fontSize: fontSize.sm,
    fontWeight: '700',
  },
  breakdownList: {
    gap: 0,
  },
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  breakdownQ: {
    color: '#e8f1f8',
    fontSize: fontSize.sm,
    fontWeight: '600',
    lineHeight: 18,
  },
  breakdownType: {
    fontSize: fontSize._2xs,
    fontWeight: '700',
    marginTop: 1,
  },
  breakdownPts: {
    fontSize: fontSize.sm,
    fontWeight: '800',
    minWidth: 34,
    textAlign: 'right',
    paddingTop: 1,
  },

  // Decorative floating emoji (summer theme)
  decorEmoji: {
    position: 'absolute',
    zIndex: 0,
  },

  // Answer comparison inside breakdown
  answerCompare: {
    marginTop: 4,
    gap: 4,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
  },
  answerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexWrap: 'wrap',
  },
  answerLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: fontSize._2xs,
    fontWeight: '600',
  },
  answerValue: {
    fontSize: fontSize._2xs,
    fontWeight: '700',
    flex: 1,
  },
})
